/**
 * Options REST API
 *
 * Access market data, manage accounts, and trade Binance Options.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type {
    AutoCancelAllOpenOrdersResponse,
    GetAutoCancelAllOpenOrdersResponse,
    GetMarketMakerProtectionConfigResponse,
    ResetMarketMakerProtectionConfigResponse,
    SetAutoCancelAllOpenOrdersResponse,
    SetMarketMakerProtectionConfigResponse,
} from '../types';

/**
 * MarketMakerEndpointsApi - axios parameter creator
 */
const MarketMakerEndpointsApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * This endpoint resets the time from which the countdown will begin to the time this messaged is received.  It should be called repeatedly as heartbeats.  Multiple heartbeats can be updated at once by specifying the underlying symbols as a list (ex. BTCUSDT,ETHUSDT) in the underlyings parameter.
         *
         * Weight(IP): 10
         *
         * Security Type: TRADE
         *
         * Notes:
         * - The response will only include underlying symbols where the heartbeat has been successfully updated.
         *
         * @summary Auto-Cancel All Open Orders (Kill-Switch) Heartbeat (TRADE)
         * @param {string} underlyings
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        autoCancelAllOpenOrders: async (
            underlyings: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'underlyings' is not null or undefined
            assertParamExists('autoCancelAllOpenOrders', 'underlyings', underlyings);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlyings !== undefined && underlyings !== null) {
                localVarQueryParameter['underlyings'] = underlyings;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/countdownCancelAllHeartBeat',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * This endpoint returns the auto-cancel parameters for each underlying symbol. Note only active auto-cancel parameters will be returned, if countdownTime is set to 0 (ie. countdownTime has been turned off), the underlying symbol and corresponding countdownTime parameter will not be returned in the response.
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * Notes:
         * - countdownTime = 0 means the function is disabled.
         *
         * @summary Get Auto-Cancel All Open Orders (Kill-Switch) Config (TRADE)
         * @param {string} [underlying] Underlying asset.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        getAutoCancelAllOpenOrders: async (
            underlying?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/countdownCancelAll',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get config for MMP.
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * @summary Get Market Maker Protection Config (TRADE)
         * @param {string} underlying Underlying asset.
         * @param {number | bigint} [recvWindow] Recv Window.
         *
         * @throws {RequiredError}
         */
        getMarketMakerProtectionConfig: async (
            underlying: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('getMarketMakerProtectionConfig', 'underlying', underlying);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/mmp',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Reset MMP, start MMP order again.
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * @summary Reset Market Maker Protection Config (TRADE)
         * @param {string} underlying
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        resetMarketMakerProtectionConfig: async (
            underlying: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('resetMarketMakerProtectionConfig', 'underlying', underlying);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/mmpReset',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * This endpoint sets the parameters of the auto-cancel feature which cancels all open orders (both market maker protection and non market maker protection order types) of the underlying symbol at the end of the specified countdown time period if no heartbeat message is sent.  After the countdown time period, all open orders will be cancelled and new orders will be rejected with error code -2010 until either a heartbeat message is sent or the auto-cancel feature is turned off by setting countdownTime to 0.
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * Notes:
         * - This rest endpoint sets up the parameters to cancel your open orders in case of an outage or disconnection.
         * - Example usage: > Call this endpoint with a countdownTime value of 10000 (10 seconds) to turn on the auto-cancel feature. If the corresponding countdownCancelAllHeartBeat endpoint is not called within 10 seconds with the specified underlying symbol, all open orders of the specified symbol will be automatically canceled. If this endpoint is called with an countdownTime of 0, the countdown timer will be stopped.
         * - The system will check all countdowns approximately every 100 milliseconds, **please note that sufficient redundancy should be considered when using this function**. We do not recommend setting the countdown time to be too precise or too small.
         *
         * @summary Set Auto-Cancel All Open Orders (Kill-Switch) Config (TRADE)
         * @param {string} underlying
         * @param {number | bigint} countdownTime Countdown time in milliseconds (ex. 1,000 for 1 second). 0 to disable the timer. Negative values (ex. -10000) are not accepted. Minimum acceptable value is 5,000
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        setAutoCancelAllOpenOrders: async (
            underlying: string,
            countdownTime: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('setAutoCancelAllOpenOrders', 'underlying', underlying);
            // verify required parameter 'countdownTime' is not null or undefined
            assertParamExists('setAutoCancelAllOpenOrders', 'countdownTime', countdownTime);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }
            if (countdownTime !== undefined && countdownTime !== null) {
                localVarQueryParameter['countdownTime'] = countdownTime;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/countdownCancelAll',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Set config for MMP. Market Maker Protection(MMP) is a set of protection mechanism for option market maker, this mechanism is able to prevent mass trading in short period time. Once market maker's account branches the threshold, the Market Maker Protection will be triggered. When Market Maker Protection triggers, all the current MMP orders will be canceled, new MMP orders will be rejected. Market maker can use this time to reevaluate market and modify order price.
         *
         * Weight(IP): 1
         *
         * Security Type: TRADE
         *
         * @summary Set Market Maker Protection Config (TRADE)
         * @param {string} underlying
         * @param {number | bigint} windowTimeInMilliseconds MMP Interval in milliseconds
         * @param {number | bigint} frozenTimeInMilliseconds MMP frozen time in milliseconds, if set to 0 manual reset is required
         * @param {number} qtyLimit quantity limit
         * @param {number} deltaLimit net delta limit
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        setMarketMakerProtectionConfig: async (
            underlying: string,
            windowTimeInMilliseconds: number | bigint,
            frozenTimeInMilliseconds: number | bigint,
            qtyLimit: number,
            deltaLimit: number,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('setMarketMakerProtectionConfig', 'underlying', underlying);
            // verify required parameter 'windowTimeInMilliseconds' is not null or undefined
            assertParamExists(
                'setMarketMakerProtectionConfig',
                'windowTimeInMilliseconds',
                windowTimeInMilliseconds
            );
            // verify required parameter 'frozenTimeInMilliseconds' is not null or undefined
            assertParamExists(
                'setMarketMakerProtectionConfig',
                'frozenTimeInMilliseconds',
                frozenTimeInMilliseconds
            );
            // verify required parameter 'qtyLimit' is not null or undefined
            assertParamExists('setMarketMakerProtectionConfig', 'qtyLimit', qtyLimit);
            // verify required parameter 'deltaLimit' is not null or undefined
            assertParamExists('setMarketMakerProtectionConfig', 'deltaLimit', deltaLimit);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }
            if (windowTimeInMilliseconds !== undefined && windowTimeInMilliseconds !== null) {
                localVarQueryParameter['windowTimeInMilliseconds'] = windowTimeInMilliseconds;
            }
            if (frozenTimeInMilliseconds !== undefined && frozenTimeInMilliseconds !== null) {
                localVarQueryParameter['frozenTimeInMilliseconds'] = frozenTimeInMilliseconds;
            }
            if (qtyLimit !== undefined && qtyLimit !== null) {
                localVarQueryParameter['qtyLimit'] = qtyLimit;
            }
            if (deltaLimit !== undefined && deltaLimit !== null) {
                localVarQueryParameter['deltaLimit'] = deltaLimit;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/mmpSet',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * MarketMakerEndpointsApi - interface
 * @interface MarketMakerEndpointsApi
 */
export interface MarketMakerEndpointsApiInterface {
    /**
     * This endpoint resets the time from which the countdown will begin to the time this messaged is received.  It should be called repeatedly as heartbeats.  Multiple heartbeats can be updated at once by specifying the underlying symbols as a list (ex. BTCUSDT,ETHUSDT) in the underlyings parameter.
     *
     * Weight(IP): 10
     *
     * Security Type: TRADE
     *
     * Notes:
     * - The response will only include underlying symbols where the heartbeat has been successfully updated.
     *
     * @summary Auto-Cancel All Open Orders (Kill-Switch) Heartbeat (TRADE)
     * @param {AutoCancelAllOpenOrdersRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApiInterface
     */
    autoCancelAllOpenOrders(
        requestParameters: AutoCancelAllOpenOrdersRequest
    ): Promise<RestApiResponse<AutoCancelAllOpenOrdersResponse>>;
    /**
     * This endpoint returns the auto-cancel parameters for each underlying symbol. Note only active auto-cancel parameters will be returned, if countdownTime is set to 0 (ie. countdownTime has been turned off), the underlying symbol and corresponding countdownTime parameter will not be returned in the response.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - countdownTime = 0 means the function is disabled.
     *
     * @summary Get Auto-Cancel All Open Orders (Kill-Switch) Config (TRADE)
     * @param {GetAutoCancelAllOpenOrdersRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApiInterface
     */
    getAutoCancelAllOpenOrders(
        requestParameters?: GetAutoCancelAllOpenOrdersRequest
    ): Promise<RestApiResponse<GetAutoCancelAllOpenOrdersResponse>>;
    /**
     * Get config for MMP.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Get Market Maker Protection Config (TRADE)
     * @param {GetMarketMakerProtectionConfigRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApiInterface
     */
    getMarketMakerProtectionConfig(
        requestParameters: GetMarketMakerProtectionConfigRequest
    ): Promise<RestApiResponse<GetMarketMakerProtectionConfigResponse>>;
    /**
     * Reset MMP, start MMP order again.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Reset Market Maker Protection Config (TRADE)
     * @param {ResetMarketMakerProtectionConfigRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApiInterface
     */
    resetMarketMakerProtectionConfig(
        requestParameters: ResetMarketMakerProtectionConfigRequest
    ): Promise<RestApiResponse<ResetMarketMakerProtectionConfigResponse>>;
    /**
     * This endpoint sets the parameters of the auto-cancel feature which cancels all open orders (both market maker protection and non market maker protection order types) of the underlying symbol at the end of the specified countdown time period if no heartbeat message is sent.  After the countdown time period, all open orders will be cancelled and new orders will be rejected with error code -2010 until either a heartbeat message is sent or the auto-cancel feature is turned off by setting countdownTime to 0.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - This rest endpoint sets up the parameters to cancel your open orders in case of an outage or disconnection.
     * - Example usage: > Call this endpoint with a countdownTime value of 10000 (10 seconds) to turn on the auto-cancel feature. If the corresponding countdownCancelAllHeartBeat endpoint is not called within 10 seconds with the specified underlying symbol, all open orders of the specified symbol will be automatically canceled. If this endpoint is called with an countdownTime of 0, the countdown timer will be stopped.
     * - The system will check all countdowns approximately every 100 milliseconds, **please note that sufficient redundancy should be considered when using this function**. We do not recommend setting the countdown time to be too precise or too small.
     *
     * @summary Set Auto-Cancel All Open Orders (Kill-Switch) Config (TRADE)
     * @param {SetAutoCancelAllOpenOrdersRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApiInterface
     */
    setAutoCancelAllOpenOrders(
        requestParameters: SetAutoCancelAllOpenOrdersRequest
    ): Promise<RestApiResponse<SetAutoCancelAllOpenOrdersResponse>>;
    /**
     * Set config for MMP. Market Maker Protection(MMP) is a set of protection mechanism for option market maker, this mechanism is able to prevent mass trading in short period time. Once market maker's account branches the threshold, the Market Maker Protection will be triggered. When Market Maker Protection triggers, all the current MMP orders will be canceled, new MMP orders will be rejected. Market maker can use this time to reevaluate market and modify order price.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Set Market Maker Protection Config (TRADE)
     * @param {SetMarketMakerProtectionConfigRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApiInterface
     */
    setMarketMakerProtectionConfig(
        requestParameters: SetMarketMakerProtectionConfigRequest
    ): Promise<RestApiResponse<SetMarketMakerProtectionConfigResponse>>;
}

/**
 * Request parameters for autoCancelAllOpenOrders operation in MarketMakerEndpointsApi.
 * @interface AutoCancelAllOpenOrdersRequest
 */
export interface AutoCancelAllOpenOrdersRequest {
    /**
     *
     * @type {string}
     * @memberof MarketMakerEndpointsApiAutoCancelAllOpenOrders
     */
    readonly underlyings: string;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiAutoCancelAllOpenOrders
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getAutoCancelAllOpenOrders operation in MarketMakerEndpointsApi.
 * @interface GetAutoCancelAllOpenOrdersRequest
 */
export interface GetAutoCancelAllOpenOrdersRequest {
    /**
     * Underlying asset.
     * @type {string}
     * @memberof MarketMakerEndpointsApiGetAutoCancelAllOpenOrders
     */
    readonly underlying?: string;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiGetAutoCancelAllOpenOrders
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getMarketMakerProtectionConfig operation in MarketMakerEndpointsApi.
 * @interface GetMarketMakerProtectionConfigRequest
 */
export interface GetMarketMakerProtectionConfigRequest {
    /**
     * Underlying asset.
     * @type {string}
     * @memberof MarketMakerEndpointsApiGetMarketMakerProtectionConfig
     */
    readonly underlying: string;

    /**
     * Recv Window.
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiGetMarketMakerProtectionConfig
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for resetMarketMakerProtectionConfig operation in MarketMakerEndpointsApi.
 * @interface ResetMarketMakerProtectionConfigRequest
 */
export interface ResetMarketMakerProtectionConfigRequest {
    /**
     *
     * @type {string}
     * @memberof MarketMakerEndpointsApiResetMarketMakerProtectionConfig
     */
    readonly underlying: string;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiResetMarketMakerProtectionConfig
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for setAutoCancelAllOpenOrders operation in MarketMakerEndpointsApi.
 * @interface SetAutoCancelAllOpenOrdersRequest
 */
export interface SetAutoCancelAllOpenOrdersRequest {
    /**
     *
     * @type {string}
     * @memberof MarketMakerEndpointsApiSetAutoCancelAllOpenOrders
     */
    readonly underlying: string;

    /**
     * Countdown time in milliseconds (ex. 1,000 for 1 second). 0 to disable the timer. Negative values (ex. -10000) are not accepted. Minimum acceptable value is 5,000
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiSetAutoCancelAllOpenOrders
     */
    readonly countdownTime: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiSetAutoCancelAllOpenOrders
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for setMarketMakerProtectionConfig operation in MarketMakerEndpointsApi.
 * @interface SetMarketMakerProtectionConfigRequest
 */
export interface SetMarketMakerProtectionConfigRequest {
    /**
     *
     * @type {string}
     * @memberof MarketMakerEndpointsApiSetMarketMakerProtectionConfig
     */
    readonly underlying: string;

    /**
     * MMP Interval in milliseconds
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiSetMarketMakerProtectionConfig
     */
    readonly windowTimeInMilliseconds: number | bigint;

    /**
     * MMP frozen time in milliseconds, if set to 0 manual reset is required
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiSetMarketMakerProtectionConfig
     */
    readonly frozenTimeInMilliseconds: number | bigint;

    /**
     * quantity limit
     * @type {number}
     * @memberof MarketMakerEndpointsApiSetMarketMakerProtectionConfig
     */
    readonly qtyLimit: number;

    /**
     * net delta limit
     * @type {number}
     * @memberof MarketMakerEndpointsApiSetMarketMakerProtectionConfig
     */
    readonly deltaLimit: number;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketMakerEndpointsApiSetMarketMakerProtectionConfig
     */
    readonly recvWindow?: number | bigint;
}

/**
 * MarketMakerEndpointsApi - object-oriented interface
 * @class MarketMakerEndpointsApi
 */
export class MarketMakerEndpointsApi implements MarketMakerEndpointsApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = MarketMakerEndpointsApiAxiosParamCreator(configuration);
    }

    /**
     * This endpoint resets the time from which the countdown will begin to the time this messaged is received.  It should be called repeatedly as heartbeats.  Multiple heartbeats can be updated at once by specifying the underlying symbols as a list (ex. BTCUSDT,ETHUSDT) in the underlyings parameter.
     *
     * Weight(IP): 10
     *
     * Security Type: TRADE
     *
     * Notes:
     * - The response will only include underlying symbols where the heartbeat has been successfully updated.
     *
     * @summary Auto-Cancel All Open Orders (Kill-Switch) Heartbeat (TRADE)
     * @param {AutoCancelAllOpenOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<AutoCancelAllOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-maker-endpoints#auto-cancel-all-open-orders Binance API Documentation}
     */
    public async autoCancelAllOpenOrders(
        requestParameters: AutoCancelAllOpenOrdersRequest
    ): Promise<RestApiResponse<AutoCancelAllOpenOrdersResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.autoCancelAllOpenOrders(
            requestParameters?.underlyings,
            requestParameters?.recvWindow
        );
        return sendRequest<AutoCancelAllOpenOrdersResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * This endpoint returns the auto-cancel parameters for each underlying symbol. Note only active auto-cancel parameters will be returned, if countdownTime is set to 0 (ie. countdownTime has been turned off), the underlying symbol and corresponding countdownTime parameter will not be returned in the response.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - countdownTime = 0 means the function is disabled.
     *
     * @summary Get Auto-Cancel All Open Orders (Kill-Switch) Config (TRADE)
     * @param {GetAutoCancelAllOpenOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetAutoCancelAllOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-maker-endpoints#get-auto-cancel-all-open-orders Binance API Documentation}
     */
    public async getAutoCancelAllOpenOrders(
        requestParameters: GetAutoCancelAllOpenOrdersRequest = {}
    ): Promise<RestApiResponse<GetAutoCancelAllOpenOrdersResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getAutoCancelAllOpenOrders(
            requestParameters?.underlying,
            requestParameters?.recvWindow
        );
        return sendRequest<GetAutoCancelAllOpenOrdersResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get config for MMP.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Get Market Maker Protection Config (TRADE)
     * @param {GetMarketMakerProtectionConfigRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetMarketMakerProtectionConfigResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-maker-endpoints#get-market-maker-protection-config Binance API Documentation}
     */
    public async getMarketMakerProtectionConfig(
        requestParameters: GetMarketMakerProtectionConfigRequest
    ): Promise<RestApiResponse<GetMarketMakerProtectionConfigResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getMarketMakerProtectionConfig(
                requestParameters?.underlying,
                requestParameters?.recvWindow
            );
        return sendRequest<GetMarketMakerProtectionConfigResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Reset MMP, start MMP order again.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Reset Market Maker Protection Config (TRADE)
     * @param {ResetMarketMakerProtectionConfigRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<ResetMarketMakerProtectionConfigResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-maker-endpoints#reset-market-maker-protection-config Binance API Documentation}
     */
    public async resetMarketMakerProtectionConfig(
        requestParameters: ResetMarketMakerProtectionConfigRequest
    ): Promise<RestApiResponse<ResetMarketMakerProtectionConfigResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.resetMarketMakerProtectionConfig(
                requestParameters?.underlying,
                requestParameters?.recvWindow
            );
        return sendRequest<ResetMarketMakerProtectionConfigResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * This endpoint sets the parameters of the auto-cancel feature which cancels all open orders (both market maker protection and non market maker protection order types) of the underlying symbol at the end of the specified countdown time period if no heartbeat message is sent.  After the countdown time period, all open orders will be cancelled and new orders will be rejected with error code -2010 until either a heartbeat message is sent or the auto-cancel feature is turned off by setting countdownTime to 0.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - This rest endpoint sets up the parameters to cancel your open orders in case of an outage or disconnection.
     * - Example usage: > Call this endpoint with a countdownTime value of 10000 (10 seconds) to turn on the auto-cancel feature. If the corresponding countdownCancelAllHeartBeat endpoint is not called within 10 seconds with the specified underlying symbol, all open orders of the specified symbol will be automatically canceled. If this endpoint is called with an countdownTime of 0, the countdown timer will be stopped.
     * - The system will check all countdowns approximately every 100 milliseconds, **please note that sufficient redundancy should be considered when using this function**. We do not recommend setting the countdown time to be too precise or too small.
     *
     * @summary Set Auto-Cancel All Open Orders (Kill-Switch) Config (TRADE)
     * @param {SetAutoCancelAllOpenOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<SetAutoCancelAllOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-maker-endpoints#set-auto-cancel-all-open-orders Binance API Documentation}
     */
    public async setAutoCancelAllOpenOrders(
        requestParameters: SetAutoCancelAllOpenOrdersRequest
    ): Promise<RestApiResponse<SetAutoCancelAllOpenOrdersResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.setAutoCancelAllOpenOrders(
            requestParameters?.underlying,
            requestParameters?.countdownTime,
            requestParameters?.recvWindow
        );
        return sendRequest<SetAutoCancelAllOpenOrdersResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Set config for MMP. Market Maker Protection(MMP) is a set of protection mechanism for option market maker, this mechanism is able to prevent mass trading in short period time. Once market maker's account branches the threshold, the Market Maker Protection will be triggered. When Market Maker Protection triggers, all the current MMP orders will be canceled, new MMP orders will be rejected. Market maker can use this time to reevaluate market and modify order price.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Set Market Maker Protection Config (TRADE)
     * @param {SetMarketMakerProtectionConfigRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<SetMarketMakerProtectionConfigResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketMakerEndpointsApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-maker-endpoints#set-market-maker-protection-config Binance API Documentation}
     */
    public async setMarketMakerProtectionConfig(
        requestParameters: SetMarketMakerProtectionConfigRequest
    ): Promise<RestApiResponse<SetMarketMakerProtectionConfigResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.setMarketMakerProtectionConfig(
                requestParameters?.underlying,
                requestParameters?.windowTimeInMilliseconds,
                requestParameters?.frozenTimeInMilliseconds,
                requestParameters?.qtyLimit,
                requestParameters?.deltaLimit,
                requestParameters?.recvWindow
            );
        return sendRequest<SetMarketMakerProtectionConfigResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }
}
